import type { MatcherOptions, RenderResult } from '@testing-library/react';
import { fireEvent, render } from '@testing-library/react';
import React, { createRef } from 'react';
import type { Store } from 'redux';
import { I18n } from '@bimone/bimtrack-core';
import { PageRenderer } from './PageRenderer';

export default abstract class BasePageObject<Props, RefType = unknown> {
  protected abstract defaultProps: Props;
  protected renderResult: RenderResult;
  protected renderer: PageRenderer;
  protected previousProps: Props | undefined;
  protected componentRef? = createRef<RefType>();

  protected constructor(renderer: PageRenderer) {
    this.renderResult = render(<div />);
    this.renderer = renderer;
  }

  /**
   * Use a real user action to modify the props OR create a new instance of the pageObject with the correct props.
   * Use only this method to re-render the component with new props when not integrated within a parent hierarchy
   * that normally propagate the modified props by actions.
   */
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected receiveProps(_props: Partial<Props>): void {}

  public get store(): Store {
    return this.renderer.store;
  }

  public get i18n(): I18n {
    return this.renderer.i18n;
  }

  public get renderedComponent(): RenderResult {
    return this.renderResult;
  }

  public getComponentRef(): RefType | undefined {
    return this.componentRef?.current as RefType;
  }

  public async given(beforeMount: (store: Store) => Promise<void>): Promise<this> {
    await beforeMount(this.store);
    return this;
  }

  public render(Component: React.ComponentType<Props>, props?: Partial<Props>, withRef = false): this {
    const newProps = { ...this.defaultProps, ...props };

    this.renderResult = this.renderer.mount(
      <Component
        ref={withRef ? this.componentRef : undefined}
        {...newProps}
      />
    );

    this.previousProps = newProps;

    return this;
  }

  protected updateProps(Component: React.ComponentType<Partial<Props>>, props: Partial<Props>): void {
    const newProps = { ...this.defaultProps, ...this.previousProps, ...props };

    this.renderer.update(
      <Component
        ref={this.componentRef}
        {...newProps}
      />,
      this.renderResult.rerender
    );
    this.previousProps = newProps;
  }

  public getByText<ElementType extends HTMLElement = HTMLElement>(text: string, args?: MatcherOptions): ElementType {
    return this.renderResult.getByText(text, args) as ElementType;
  }

  public getByLabelText<ElementType extends HTMLElement = HTMLElement>(
    text: string,
    args?: MatcherOptions
  ): ElementType {
    return this.renderResult.getByLabelText(text, args) as ElementType;
  }

  public getByPlaceholderText<ElementType extends HTMLElement = HTMLElement>(
    text: string,
    args?: MatcherOptions
  ): ElementType {
    return this.renderResult.getByPlaceholderText(text, args) as ElementType;
  }

  public getByTestId<ElementType extends HTMLElement = HTMLElement>(
    testId: string,
    args?: MatcherOptions
  ): ElementType {
    return this.renderResult.getByTestId(testId, args) as ElementType;
  }

  public getAllByTestId<ElementType extends HTMLElement = HTMLElement>(
    testId: string,
    args?: MatcherOptions
  ): ElementType[] {
    return this.renderResult.getAllByTestId(testId, args) as ElementType[];
  }

  public queryByText<ElementType extends HTMLElement = HTMLElement>(
    text: string,
    args?: MatcherOptions
  ): ElementType | null {
    return this.renderResult.queryByText(text, args) as ElementType;
  }

  public queryByTestId<ElementType extends HTMLElement = HTMLElement>(
    testId: string,
    args?: MatcherOptions
  ): ElementType | null {
    return this.renderResult.queryByTestId(testId, args) as ElementType;
  }

  public clickByTestId(testId: string, args?: MatcherOptions): void {
    const foundElement = this.queryByTestId(testId, args);

    if (foundElement) {
      fireEvent.click(foundElement);
    }
  }

  public clickByText(text: string, args?: MatcherOptions): void {
    const foundElement = this.queryByText(text, args);

    if (foundElement) {
      fireEvent.click(foundElement);
    }
  }

  public debug(maxLength = 500000): void {
    this.renderResult.debug(undefined, maxLength);
  }

  public debugElement(element: HTMLElement, maxLength = 500000): void {
    this.renderResult.debug(element, maxLength);
  }

  public debugElementByTestId(testId: string, args?: MatcherOptions, maxLength = 500000): void {
    this.renderResult.debug(this.getByTestId(testId, args), maxLength);
  }
}
